import React, { useState, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Divider
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { AppContext } from "../../../context/AppContext";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 440,
  bgcolor: "#fff",
  borderRadius: 2,
  p: 3,
  boxShadow: "0 12px 30px rgba(0,0,0,0.25)"
};

const GenerateTemporaryKey = ({
  open,
  onClose,
  user,
  onDone,
  empresaNombre = "CMSOLTEC",
  loginUrl = "https://cmsoltec.com/login"
}) => {
  const { accessToken } = useContext(AppContext);

  const [loading, setLoading] = useState(false);
  const [sendingMail, setSendingMail] = useState(false);
  const [clave, setClave] = useState("");
  const [snack, setSnack] = useState({ open: false, msg: "", type: "success" });

  const parseJsonSafe = async (res) => {
    try {
      return await res.json();
    } catch {
      return { ok: false, error: "Respuesta inválida del servidor" };
    }
  };

  const authFetch = async (url, options = {}) => {
    const headers = {
      Accept: "application/json",
      ...(options.headers || {}),
      Authorization: `Bearer ${accessToken}`
    };

    return fetch(url, {
      ...options,
      credentials: "omit",
      headers
    });
  };

  const generarClave = async () => {
    if (!user) return;

    setLoading(true);

    try {
      const res = await authFetch("/sistema/regenerar-clave-temporal/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ id: user.id })
      });

      const data = await parseJsonSafe(res);

      if (res.ok && data.ok) {
        setClave(data.clave_temporal);
        onDone("Clave temporal generada correctamente", "success");
      } else {
        onDone(data?.error || "Error al generar la clave", "error");
      }
    } catch {
      onDone("Error de conexión", "error");
    } finally {
      setLoading(false);
    }
  };

  const copiarClave = async () => {
    await navigator.clipboard.writeText(clave);
    setSnack({ open: true, msg: "Clave copiada al portapapeles", type: "info" });
  };

  const enviarWhatsApp = () => {
    const numero = user?.telefono || "";

    const mensaje = encodeURIComponent(
      `Hola ${user?.nombre || ""},\n\nTe han generado una nueva clave temporal de acceso en ${empresaNombre}.\n\nCorreo: ${user?.email || ""}\nClave temporal: ${clave}\n\nPuedes ingresar aquí:\n${loginUrl}`
    );

    window.open(`https://wa.me/593${numero}?text=${mensaje}`, "_blank");
  };

  const enviarCorreo = async () => {
    if (!user || sendingMail) return;

    try {
      setSendingMail(true);

      const res = await authFetch("/sistema/enviar-clave-correo/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ id: user.id, login_url: loginUrl })
      });

      const data = await parseJsonSafe(res);

      if (res.ok && data.ok) {
        onDone(data?.msg || "Clave enviada por correo", "success");
      } else {
        onDone(data?.error || "Error al enviar correo", "error");
      }
    } catch {
      onDone("Error de conexión", "error");
    } finally {
      setSendingMail(false);
    }
  };

  const cerrar = () => {
    if (loading || sendingMail) return;
    setClave("");
    onClose();
  };

  return (
    <>
      <Modal open={open} onClose={cerrar}>
        <Box sx={modalStyle}>
          <Typography fontWeight={700} fontSize="1.1rem" mb={1}>
            Generar clave temporal
          </Typography>

          <Typography color="#555" mb={2}>
            Usuario: <strong>{user?.nombre || "—"}</strong>
          </Typography>

          {!clave && (
            <Button
              fullWidth
              disabled={loading}
              onClick={generarClave}
              sx={{
                bgcolor: "#1565C0",
                color: "#fff",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { bgcolor: "#0D47A1" }
              }}
            >
              Generar clave temporal
            </Button>
          )}

          {clave && (
            <>
              <Divider sx={{ my: 2 }} />

              <Typography fontSize="0.9rem" mb={1}>
                Nueva clave temporal
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: "#F5F7FA",
                  p: 1.5,
                  borderRadius: 1,
                  fontWeight: 700,
                  letterSpacing: 1
                }}
              >
                {clave}

                <Tooltip title="Copiar">
                  <IconButton onClick={copiarClave}>
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
                <Button
                  fullWidth
                  startIcon={<WhatsAppIcon />}
                  onClick={enviarWhatsApp}
                  sx={{
                    bgcolor: "#E8F5E9",
                    color: "#2E7D32",
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#DFF0E3" }
                  }}
                >
                  WhatsApp
                </Button>

                <Button
                  fullWidth
                  startIcon={<EmailIcon />}
                  onClick={enviarCorreo}
                  disabled={sendingMail}
                  sx={{
                    bgcolor: "#E3F2FD",
                    color: "#1565C0",
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#D6EAF8" }
                  }}
                >
                  Correo
                </Button>
              </Box>
            </>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button onClick={cerrar} sx={{ textTransform: "none" }} disabled={loading || sendingMail}>
              Cerrar
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snack.type} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GenerateTemporaryKey;